import { API_ENDPOINTS } from "@/config/api";

type AuthEndpoint = "login" | "register" | "refresh" | "guestLogin";

const AUTH_PROXY_ENDPOINTS: Record<AuthEndpoint, string> = {
    login: API_ENDPOINTS.auth.login,
    register: API_ENDPOINTS.auth.register,
    refresh: API_ENDPOINTS.auth.refresh,
    guestLogin: API_ENDPOINTS.auth.guestLogin,
};

const AUTH_PUBLIC_ORIGIN = String(
    (import.meta as any)?.env?.VITE_AUTH_SERVICE_PUBLIC_URL || "https://auth-service-ak0f.onrender.com"
).replace(/\/+$/, "");

const AUTH_DIRECT_ENDPOINTS: Record<AuthEndpoint, string> = {
    login: `${AUTH_PUBLIC_ORIGIN}/auth/v1/login`,
    register: `${AUTH_PUBLIC_ORIGIN}/auth/v1/register`,
    refresh: `${AUTH_PUBLIC_ORIGIN}/auth/v1/refresh`,
    guestLogin: `${AUTH_PUBLIC_ORIGIN}/auth/v1/guest-login`,
};

const TRANSIENT_GATEWAY_STATUSES = new Set([502, 503, 504]);
const RETRYABLE_AUTH_ENDPOINTS = new Set<AuthEndpoint>(["refresh", "guestLogin"]);
const DIRECT_FALLBACK_STATUSES: Partial<Record<AuthEndpoint, Set<number>>> = {
    login: new Set([404, 405, 429, ...TRANSIENT_GATEWAY_STATUSES]),
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function fetchAuthWithFallback(
    endpoint: AuthEndpoint,
    init: RequestInit
): Promise<Response> {
    let firstError: unknown = null;
    const shouldRetry = RETRYABLE_AUTH_ENDPOINTS.has(endpoint);

    try {
        const proxyResponse = await fetch(AUTH_PROXY_ENDPOINTS[endpoint], init);
        if (DIRECT_FALLBACK_STATUSES[endpoint]?.has(proxyResponse.status)) {
            try {
                return await fetch(AUTH_DIRECT_ENDPOINTS[endpoint], init);
            } catch {
                return proxyResponse;
            }
        }

        if (!shouldRetry || !TRANSIENT_GATEWAY_STATUSES.has(proxyResponse.status)) {
            return proxyResponse;
        }
    } catch (err) {
        firstError = err;
        if (!shouldRetry) {
            throw err;
        }
    }

    await wait(500);

    try {
        const retryResponse = await fetch(AUTH_PROXY_ENDPOINTS[endpoint], init);
        if (!TRANSIENT_GATEWAY_STATUSES.has(retryResponse.status)) {
            return retryResponse;
        }
    } catch {
        // Fall through to direct public auth endpoint.
    }

    try {
        return await fetch(AUTH_DIRECT_ENDPOINTS[endpoint], init);
    } catch {
        if (firstError instanceof Error) {
            throw firstError;
        }
        throw new Error("Auth request failed");
    }
}
